import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LayoutDashboard,
  Map as MapIcon,
  BarChart3,
  Leaf,
  Scale,
  Database,
  Settings,
  LogOut,
  AlertTriangle,
  FileText,
  ScrollText,
  ShieldCheck,
  BookOpen,
  Activity,
  Truck,
  Trash2,
} from "lucide-react";
import BrandMark from "@/components/salnes/BrandMark";
import LanguageSelector from "@/components/salnes/LanguageSelector";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import "@/i18n/fase2";
import "@/i18n/fase3";
import "@/i18n/fase4";
import "@/i18n/fase5";
import "@/i18n/fase6";
import "@/i18n/fase8";
import "@/i18n/fase9";

const NAV = [
  { to: "/app/dashboard", icon: LayoutDashboard, key: "overview", enabled: true },
  { to: "/app/geoportal", icon: MapIcon, keyRaw: "fase2.nav_geoportal", enabled: true, testid: "sidebar-geoportal" },
  { to: "/app/frota", icon: Truck, keyRaw: "fase8.frota.nav", enabled: true, testid: "sidebar-frota" },
  { to: "/app/contedores", icon: Trash2, keyRaw: "fase9.contedores.nav", enabled: true, testid: "sidebar-contedores" },
  { to: "/app/esg", icon: BarChart3, keyRaw: "fase2.nav_esg", enabled: true, testid: "sidebar-esg" },
  { to: "/app/pegada", icon: Leaf, keyRaw: "fase3.sidebar_pegada", enabled: true, testid: "sidebar-pegada" },
  { to: "/app/pesaxes", icon: Scale, keyRaw: "fase3.sidebar_pesaxes", enabled: true, testid: "sidebar-pesaxes", roles: ["ADMIN", "SUPERVISOR"] },
  { to: "/app/incidencias", icon: AlertTriangle, keyRaw: "fase4.sidebar_incidencias", enabled: true, testid: "sidebar-incidencias" },
  { to: "/app/inventario", icon: Database, keyRaw: "fase4.sidebar_inventario", enabled: true, testid: "sidebar-inventario" },
  { to: "/app/informe", icon: FileText, keyRaw: "fase4.sidebar_informe", enabled: true, testid: "sidebar-informe" },
  { to: "/app/auditoria", icon: ScrollText, keyRaw: "fase4.sidebar_auditoria", enabled: true, testid: "sidebar-auditoria", roles: ["ADMIN"] },
  { to: "/app/admin", icon: ShieldCheck, keyRaw: "fase5.sidebar_admin", enabled: true, testid: "sidebar-admin", roles: ["ADMIN"] },
  { to: "/app/monitoring", icon: Activity, keyRaw: "fase6.sidebar_monitoring", enabled: true, testid: "sidebar-monitoring", roles: ["ADMIN"] },
];

export default function AppShell() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-salnes-ivory flex">
      {/* Sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden lg:flex flex-col w-[248px] shrink-0 border-r hairline bg-white"
      >
        <div className="h-16 flex items-center gap-3 px-5 border-b hairline">
          <BrandMark size={30} />
          <div className="leading-tight">
            <div className="font-serif text-[15px] text-salnes-ink" data-testid="sidebar-brand-name">{t("brand.name")}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">
              {t("sidebar.panel_subtitle")}
            </div>
          </div>
        </div>
        <nav className="p-3 flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone px-3 py-2">
            {t("sidebar.modules_header")}
          </div>
          {NAV.map((item) => {
            if (item.roles && !item.roles.includes(user?.role)) return null;
            const label = item.keyRaw ? t(item.keyRaw) : t(`sidebar.${item.key}`);
            if (!item.enabled) {
              return (
                <div
                  key={item.to}
                  data-testid={item.testid}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-salnes-stone/70 cursor-not-allowed"
                >
                  <item.icon size={16} strokeWidth={1.6} />
                  <span className="flex-1">{label}</span>
                  <span className="text-[9px] uppercase tracking-[0.16em] bg-salnes-sand/60 text-salnes-ink/60 px-1.5 py-0.5 rounded">
                    {t("fase2.build_phase")}
                  </span>
                </div>
              );
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid || `sidebar-${item.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                    isActive
                      ? "bg-salnes-atlantic/8 text-salnes-atlantic"
                      : "text-salnes-ink/70 hover:bg-salnes-sand/40 hover:text-salnes-ink"
                  }`
                }
              >
                <item.icon size={16} strokeWidth={1.6} />
                {label}
              </NavLink>
            );
          })}
          <div className="mt-6 text-[10px] uppercase tracking-[0.22em] text-salnes-stone px-3 py-2">
            {t("sidebar.account_header")}
          </div>
          <NavLink
            to="/app/settings"
            data-testid="sidebar-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive
                  ? "bg-salnes-atlantic/8 text-salnes-atlantic"
                  : "text-salnes-ink/70 hover:bg-salnes-sand/40 hover:text-salnes-ink"
              }`
            }
          >
            <Settings size={16} strokeWidth={1.6} /> {t("sidebar.settings")}
          </NavLink>
        </nav>
        <div className="p-4 border-t hairline">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-full bg-salnes-atlantic text-white text-xs font-medium flex items-center justify-center">
              {user?.name?.[0] || "?"}
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-sm text-salnes-ink truncate" data-testid="sidebar-user-name">
                {user?.name}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone">
                {t(`dashboard.role_${user?.role || "MANCOMUNIDADE"}`)}
              </div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full rounded-full text-salnes-stone hover:text-salnes-ink"
            onClick={handleLogout}
            data-testid="sidebar-logout"
          >
            <LogOut size={13} className="mr-2" /> {t("nav.logout")}
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 border-b hairline bg-white flex items-center justify-between px-6">
          <div className="lg:hidden flex items-center gap-3">
            <BrandMark size={28} />
            <div className="font-serif text-[15px]" data-testid="topbar-brand-name">{t("brand.name")}</div>
          </div>
          <div className="hidden lg:block text-[13px] text-salnes-stone">
            {t("dashboard.welcome")}, <span className="text-salnes-ink font-medium">{user?.name}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSelector />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="lg:hidden text-salnes-stone"
              data-testid="topbar-logout"
            >
              <LogOut size={14} />
            </Button>
          </div>
        </header>
        <main className="flex-1 p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
